import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  FileText, BookOpen, Users, Shield, Table, ListOrdered, 
  Quote, Mail, AlertCircle, CheckCircle, Info, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function AuthorGuidelinesPage() {
  return (
    <div data-testid="author-guidelines-page" className="min-h-screen bg-background">
      {/* Page Header */}
      <div className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <Badge className="bg-white/20 text-white border-0 mb-4">
            Guide de soumission
          </Badge>
          <h1 className="font-serif text-3xl md:text-4xl font-bold mb-4">
            Instructions aux auteurs
          </h1>
          <p className="text-lg text-primary-foreground/90 max-w-3xl">
            Mise en page et style pour la soumission de manuscrits au Journal de pratiques 
            et recherches en psychothérapie.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Format du manuscrit */}
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Format du manuscrit
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm font-medium mb-1">Fichier</p>
                    <p className="text-sm text-muted-foreground">
                      Word (.docx) ou LaTeX (.tex)
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium mb-1">Langue de publication</p>
                      <p className="text-sm text-muted-foreground">
                        Le JPRP publie exclusivement en français. Tous les manuscrits doivent être 
                        rédigés en français. Un résumé (abstract) en anglais est vivement recommandé 
                        afin de favoriser la visibilité internationale de l'article.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Structure obligatoire */}
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <ListOrdered className="w-5 h-5 text-primary" />
                  Structure obligatoire
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Page de titre */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">1</span>
                    Page de titre
                  </h3>
                  <ul className="ml-8 space-y-1 text-sm text-muted-foreground list-disc">
                    <li>Titre de l'article (clair, concis)</li>
                    <li>Nom(s) de l'auteur(s)</li>
                    <li>Affiliation(s)</li>
                    <li>Adresse email de correspondance</li>
                  </ul>
                </div>

                {/* Résumé */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">2</span>
                    Résumé
                  </h3>
                  <ul className="ml-8 space-y-1 text-sm text-muted-foreground list-disc">
                    <li>150 à 250 mots</li>
                    <li>En français</li>
                    <li>4 à 6 mots-clés</li>
                  </ul>
                </div>

                {/* Corps de l'article */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">3</span>
                    Corps de l'article
                  </h3>
                  <ul className="ml-8 space-y-1 text-sm text-muted-foreground list-disc">
                    <li>Introduction</li>
                    <li>Méthodologie (ou Présentation du cas / Protocole)</li>
                    <li>Résultats (ou Observations)</li>
                    <li>Discussion</li>
                    <li>Conclusion</li>
                  </ul>
                </div>

                {/* Références */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm flex items-center justify-center">4</span>
                    Références
                  </h3>
                  <p className="ml-8 text-sm text-muted-foreground mb-3">
                    Style APA 7e édition obligatoire.
                  </p>
                  <div className="ml-8 p-4 bg-muted/50 rounded-lg">
                    <p className="text-xs font-medium mb-2 text-muted-foreground">Exemples :</p>
                    <p className="text-sm font-mono mb-2">
                      Beck, A. T., & Dozois, D. J. A. (2011). Cognitive therapy: Current status and future directions. <em>Annual Review of Medicine, 62</em>, 397–409.
                    </p>
                    <p className="text-sm font-mono">
                      World Health Organization. (2020). <em>Guidelines on mental health at work</em>. WHO.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tableaux et figures */}
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Table className="w-5 h-5 text-primary" />
                  Tableaux et figures
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
                    <span className="text-sm">Inclus dans le texte à leur place logique</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
                    <div className="text-sm">
                      <span className="font-medium">Tableaux :</span> Titrés en haut 
                      <span className="text-muted-foreground"> (« Tableau 1 : Données principales... »)</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
                    <div className="text-sm">
                      <span className="font-medium">Figures :</span> Titrées en bas 
                      <span className="text-muted-foreground"> (« Figure 1. Courbe d'évolution... »)</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5" />
                    <span className="text-sm">Chaque tableau ou figure doit être cité dans le texte</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Éthique */}
            <Card className="border-amber-200 bg-amber-50/30">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-600" />
                  Éthique
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div className="text-sm">
                      <span className="font-medium">Consentement éclairé :</span>
                      <p className="text-muted-foreground mt-1">
                        Toute étude impliquant des personnes doit préciser l'obtention du consentement 
                        éclairé et, si nécessaire, l'approbation d'un comité d'éthique.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div className="text-sm">
                      <span className="font-medium">Études de cas :</span>
                      <p className="text-muted-foreground mt-1">
                        L'anonymisation complète est requise pour toutes les études de cas.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div className="text-sm">
                      <span className="font-medium">Utilisation de l'IA :</span>
                      <p className="text-muted-foreground mt-1">
                        Une déclaration relative à l'utilisation de l'IA doit figurer dans une section 
                        distincte du manuscrit (par exemple après les remerciements ou avant les références).
                      </p>
                    </div>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Template Download */}
            <Card className="bg-primary text-primary-foreground border-0">
              <CardContent className="p-6 text-center space-y-4">
                <Download className="w-10 h-10 mx-auto opacity-90" />
                <div>
                  <h3 className="font-serif font-bold text-lg">Template Word JPRP</h3>
                  <p className="text-sm text-primary-foreground/80 mt-1">
                    Utilisez ce modèle pour la mise en forme de votre manuscrit
                  </p>
                </div>
                <a href="/JPRP-template-manuscrit.docx" download>
                  <Button variant="secondary" className="w-full gap-2" data-testid="download-template-btn">
                    <Download className="w-4 h-4" />
                    Télécharger le template
                  </Button>
                </a>
                <p className="text-xs text-primary-foreground/60">
                  Format Word (.docx) — Compatible Microsoft Word, LibreOffice, Google Docs
                </p>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="bg-primary/5 border-primary/20">
              <CardHeader className="pb-3">
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <Mail className="w-5 h-5 text-primary" />
                  Contact éditorial
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl font-serif font-bold text-primary">VL</span>
                  </div>
                  <p className="font-semibold">Victor Lopez-Hernandez</p>
                  <p className="text-sm text-muted-foreground mb-2">Rédacteur en chef</p>
                  <a 
                    href="mailto:victor.loher@vmlh-tcc.fr"
                    className="text-sm text-primary hover:underline"
                  >
                    victor.loher@vmlh-tcc.fr
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Checklist rapide */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="font-serif text-lg">Checklist de soumission</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {[
                    'Template JPRP utilisé',
                    'Format Word (.docx) ou LaTeX (.tex)',
                    'Page de titre complète',
                    'Résumé 150-250 mots',
                    '4-6 mots-clés',
                    'Références APA 7e édition',
                    'Déclaration éthique',
                    'Déclaration IA (si applicable)'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <div className="w-4 h-4 border rounded flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Types d'articles */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="font-serif text-lg">Types acceptés</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {[
                    { type: 'Recherche empirique', desc: 'Études quantitatives/qualitatives' },
                    { type: 'Étude de cas', desc: 'Analyses cliniques détaillées' },
                    { type: 'Revue de littérature', desc: 'Synthèses et méta-analyses' },
                    { type: 'Note de pratique', desc: 'Retours d\'expérience' },
                    { type: 'Protocole', desc: 'Méthodologies de recherche' }
                  ].map((item, idx) => (
                    <div key={idx} className="p-2 bg-muted/50 rounded">
                      <p className="text-sm font-medium">{item.type}</p>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
